import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  MoonIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { dailyActivityAPI } from '../../services/api'
import SleepPhaseBar from './SleepPhaseBar'

// --- Sleep field definitions ---
const SLEEP_FIELDS = [
  { key: 'sleep_duration_minutes', labelKey: 'wellness.totalSleep', label: 'Total Sleep (min)' },
  { key: 'sleep_deep_minutes', labelKey: 'wellness.deepSleep', label: 'Deep (min)' },
  { key: 'sleep_light_minutes', labelKey: 'wellness.lightSleep', label: 'Light (min)' },
  { key: 'sleep_rem_minutes', labelKey: 'wellness.remSleep', label: 'REM (min)' },
  { key: 'sleep_awake_minutes', labelKey: 'wellness.awakeSleep', label: 'Awake (min)' },
]

// --- Helper: format minutes as Xh Ym ---
function formatDuration(mins) {
  if (mins == null) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

// --- Helper: check if log has sleep data ---
function hasSleepData(log) {
  return log && (
    log.sleep_duration_minutes != null ||
    log.sleep_deep_minutes != null ||
    log.sleep_light_minutes != null ||
    log.sleep_rem_minutes != null ||
    log.sleep_awake_minutes != null
  )
}

// --- VH-65: Sleep Photo Upload (follows BodyMeasurementForm pattern) ---

function SleepPhotoUpload({ onDataExtracted }) {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState(null)

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsAnalyzing(true)
    setError(null)

    try {
      const response = await dailyActivityAPI.uploadScreenshot(file)
      const data = response.data
      onDataExtracted(data)
    } catch (err) {
      console.error('Sleep screenshot analysis failed:', err)
      setError(
        err.response?.data?.detail ||
        t('wellness.screenshotFailed', 'Could not extract sleep data. Please try a different screenshot.')
      )
    } finally {
      setIsAnalyzing(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">
            {t('wellness.uploadSleepScreenshot', 'Upload Sleep Screenshot')}
          </h4>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          {t('wellness.uploadHint', 'Upload a Garmin sleep screenshot to automatically extract your sleep data.')}
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic"
          onChange={handleUpload}
          className="hidden"
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isAnalyzing}
          className="btn-primary flex items-center text-sm"
        >
          {isAnalyzing ? (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              {t('wellness.analyzing', 'Analyzing...')}
            </>
          ) : (
            <>
              <PhotoIcon className="h-4 w-4 mr-2" />
              {t('wellness.choosePhoto', 'Choose Photo')}
            </>
          )}
        </button>

        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// --- VH-65: Latest Sleep Card (colored squares like DailyActivityCard) ---

function LatestSleepCard() {
  const { t } = useTranslation()

  const { data, isLoading } = useQuery({
    queryKey: ['dailyActivityLatest'],
    queryFn: () => dailyActivityAPI.getLatest(),
    select: (res) => res.data,
    refetchOnMount: 'always',
  })

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body animate-pulse">
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  const log = data?.log
  const daysAgo = data?.days_ago

  if (!log || !hasSleepData(log)) return null

  const totalMinutes = log.sleep_duration_minutes
  const hours = totalMinutes ? Math.floor(totalMinutes / 60) : null
  const mins = totalMinutes ? totalMinutes % 60 : null

  const sleepMetrics = [
    { key: 'total', value: totalMinutes, display: hours != null ? `${hours}h ${mins}m` : null, color: 'text-purple-600', bg: 'bg-purple-50', labelKey: 'wellness.totalSleep', label: 'Total Sleep' },
    { key: 'deep', value: log.sleep_deep_minutes, display: formatDuration(log.sleep_deep_minutes), color: 'text-indigo-600', bg: 'bg-indigo-50', labelKey: 'wellness.deepSleep', label: 'Deep' },
    { key: 'light', value: log.sleep_light_minutes, display: formatDuration(log.sleep_light_minutes), color: 'text-sky-600', bg: 'bg-sky-50', labelKey: 'wellness.lightSleep', label: 'Light' },
    { key: 'rem', value: log.sleep_rem_minutes, display: formatDuration(log.sleep_rem_minutes), color: 'text-violet-600', bg: 'bg-violet-50', labelKey: 'wellness.remSleep', label: 'REM' },
    { key: 'awake', value: log.sleep_awake_minutes, display: formatDuration(log.sleep_awake_minutes), color: 'text-gray-600', bg: 'bg-gray-100', labelKey: 'wellness.awakeSleep', label: 'Awake' },
  ].filter((m) => m.value != null)

  const hasSleepPhases = log.sleep_deep_minutes != null ||
    log.sleep_light_minutes != null ||
    log.sleep_rem_minutes != null

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-gray-900">
            {daysAgo === 0
              ? t('wellness.latestSleepToday', "Today's Sleep")
              : t('wellness.latestSleep', 'Latest Sleep')
            }
          </h4>
          <div className="text-sm text-gray-500">
            {new Date(log.log_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            {daysAgo > 2 && (
              <span className="ml-2 text-amber-600">
                <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                {daysAgo} {t('activity.daysAgo', 'days ago')}
              </span>
            )}
          </div>
        </div>

        {/* Sleep metrics in colored squares */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {sleepMetrics.map((m) => (
            <div key={m.key} className={`${m.bg} rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-1">
                <MoonIcon className={`h-4 w-4 ${m.color}`} />
                <span className="text-xs text-gray-500">{t(m.labelKey, m.label)}</span>
              </div>
              <p className={`text-lg font-semibold ${m.color}`}>{m.display}</p>
            </div>
          ))}
        </div>

        {/* Sleep phase bar */}
        {hasSleepPhases && (
          <div className="mt-3">
            <SleepPhaseBar log={log} />
          </div>
        )}

        <p className="text-xs text-gray-400 mt-2">
          {t('wellness.basedOnGarmin', 'Based on Garmin data')}
        </p>
      </div>
    </div>
  )
}

// --- VH-65: Sleep Entry Form (manual add/edit) ---

function SleepEntryForm({ initialData, onSave, onCancel, isPending, t }) {
  const [form, setForm] = useState(() => {
    if (initialData) {
      return {
        log_date: initialData.log_date || new Date().toISOString().split('T')[0],
        ...SLEEP_FIELDS.reduce((acc, f) => {
          acc[f.key] = initialData[f.key] ?? ''
          return acc
        }, {}),
      }
    }
    return {
      log_date: new Date().toISOString().split('T')[0],
      ...SLEEP_FIELDS.reduce((acc, f) => { acc[f.key] = ''; return acc }, {}),
    }
  })

  const [validationError, setValidationError] = useState(null)

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    setValidationError(null)

    const payload = { log_date: form.log_date }
    let hasAtLeastOneField = false

    SLEEP_FIELDS.forEach((f) => {
      const val = form[f.key]
      if (val !== '' && val !== null && val !== undefined) {
        payload[f.key] = Number(val)
        hasAtLeastOneField = true
      }
    })

    if (!hasAtLeastOneField) {
      setValidationError(t('wellness.atLeastOneField', 'Please enter at least one sleep metric.'))
      return
    }

    onSave(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('common.date', 'Date')}
        </label>
        <input
          type="date"
          value={form.log_date}
          onChange={(e) => updateField('log_date', e.target.value)}
          className="input text-sm w-full max-w-xs"
          required
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {SLEEP_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs text-gray-500 mb-1">{t(f.labelKey, f.label)}</label>
            <input
              type="number"
              value={form[f.key]}
              onChange={(e) => updateField(f.key, e.target.value)}
              className="input text-sm w-full"
              placeholder="—"
              min="0"
            />
          </div>
        ))}
      </div>
      {validationError && (
        <p className="text-sm text-red-600">{validationError}</p>
      )}
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={isPending} className="btn-primary flex items-center text-sm">
          <CheckIcon className="h-4 w-4 mr-1" />
          {isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">
          <XMarkIcon className="h-4 w-4 mr-1 inline" />
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  )
}

// --- Delete Confirmation ---

function DeleteConfirmDialog({ onConfirm, onCancel, isPending, t }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
      <span className="text-sm text-red-700">{t('common.confirmDelete', 'Are you sure you want to delete this entry?')}</span>
      <div className="flex gap-2 ml-3">
        <button onClick={onConfirm} disabled={isPending} className="px-3 py-1 bg-red-600 text-on-neutral text-sm rounded hover:bg-red-700">
          {t('common.delete', 'Delete')}
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-surface-container text-gray-600 text-sm rounded border hover:bg-gray-50">
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  )
}

// --- VH-65: Sleep History (multi-week, mirrors MultiWeekActivityHistory) ---

function SleepHistory() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [expandedWeek, setExpandedWeek] = useState(null)
  const [weeksToLoad, setWeeksToLoad] = useState(4)
  const [editingLogId, setEditingLogId] = useState(null)
  const [deletingLogId, setDeletingLogId] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)

  const { data: multiWeekData, isLoading } = useQuery({
    queryKey: ['activityMultiWeek', weeksToLoad],
    queryFn: () => dailyActivityAPI.getMultiWeekSummary({ weeks: weeksToLoad }),
    select: (res) => res.data,
    refetchOnMount: 'always',
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['activityMultiWeek'] })
    queryClient.invalidateQueries({ queryKey: ['dailyActivityLatest'] })
  }

  const createMutation = useMutation({
    mutationFn: (data) => dailyActivityAPI.createLog(data),
    onSuccess: () => {
      invalidateAll()
      setShowAddForm(false)
      toast.success(t('wellness.saved', 'Sleep entry saved'))
    },
    onError: (err) => toast.error(err?.response?.data?.detail || t('common.error', 'Error')),
  })

  const updateMutation = useMutation({
    mutationFn: ({ logId, data }) => dailyActivityAPI.updateLog(logId, data),
    onSuccess: () => {
      invalidateAll()
      setEditingLogId(null)
      toast.success(t('wellness.updated', 'Sleep entry updated'))
    },
    onError: (err) => toast.error(err?.response?.data?.detail || t('common.error', 'Error')),
  })

  const deleteMutation = useMutation({
    mutationFn: (logId) => dailyActivityAPI.deleteLog(logId),
    onSuccess: () => {
      invalidateAll()
      setDeletingLogId(null)
      toast.success(t('wellness.deleted', 'Sleep entry deleted'))
    },
    onError: (err) => toast.error(err?.response?.data?.detail || t('common.error', 'Error')),
  })

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body animate-pulse">
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  const weeks = multiWeekData?.weeks || []

  // Filter daily logs to only those with sleep data
  const weeksWithSleep = weeks.map((week) => {
    const sleepLogs = (week.daily_logs || []).filter(hasSleepData)
    return { ...week, daily_logs: sleepLogs, days_with_sleep: sleepLogs.length }
  })

  const formatWeekLabel = (start, end) => {
    const s = new Date(start + 'T00:00:00')
    const e = new Date(end + 'T00:00:00')
    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{t('wellness.sleepHistory', 'Sleep History')}</h4>
        <button
          onClick={() => { setShowAddForm(!showAddForm); setEditingLogId(null); setDeletingLogId(null) }}
          className="btn-primary text-sm flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          {t('wellness.addEntry', 'Add Entry')}
        </button>
      </div>

      {showAddForm && (
        <SleepEntryForm
          onSave={(data) => createMutation.mutate(data)}
          onCancel={() => setShowAddForm(false)}
          isPending={createMutation.isPending}
          t={t}
        />
      )}

      {weeksWithSleep.every((w) => w.days_with_sleep === 0) && !showAddForm && (
        <div className="text-sm text-gray-400 text-center py-4">
          {t('wellness.noSleepEntries', 'No sleep entries yet. Upload a screenshot or add an entry manually.')}
        </div>
      )}

      {weeksWithSleep.map((week, idx) => {
        const isExpanded = expandedWeek === idx
        const hasData = week.days_with_sleep > 0

        if (!hasData) return null

        return (
          <div key={idx} className="card overflow-hidden">
            <div
              className="card-body cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedWeek(isExpanded ? null : idx)}
            >
              {/* Summary row */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatWeekLabel(week.period_start, week.period_end)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {week.days_with_sleep} {t('wellness.nightsTracked', 'nights tracked')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Average sleep duration for the week */}
                  {week.daily_logs.length > 0 && (() => {
                    const sleepDurations = week.daily_logs
                      .filter((l) => l.sleep_duration_minutes != null)
                      .map((l) => l.sleep_duration_minutes)
                    if (sleepDurations.length === 0) return null
                    const avg = Math.round(sleepDurations.reduce((a, b) => a + b, 0) / sleepDurations.length)
                    return (
                      <span className="text-purple-600 font-semibold text-sm">
                        {formatDuration(avg)} <span className="text-xs font-normal text-gray-400">{t('wellness.avgPerNight', 'avg/night')}</span>
                      </span>
                    )
                  })()}
                  {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded: daily sleep logs */}
              {isExpanded && (
                <div className="mt-4 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                  <div className="space-y-2">
                    {week.daily_logs.map((log) => (
                      <div key={log.id}>
                        {editingLogId === log.id ? (
                          <SleepEntryForm
                            initialData={log}
                            onSave={(data) => updateMutation.mutate({ logId: log.id, data })}
                            onCancel={() => setEditingLogId(null)}
                            isPending={updateMutation.isPending}
                            t={t}
                          />
                        ) : deletingLogId === log.id ? (
                          <DeleteConfirmDialog
                            onConfirm={() => deleteMutation.mutate(log.id)}
                            onCancel={() => setDeletingLogId(null)}
                            isPending={deleteMutation.isPending}
                            t={t}
                          />
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                            <span className="font-medium text-gray-700 w-20">
                              {new Date(log.log_date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                            </span>
                            <div className="flex gap-3 text-xs flex-wrap justify-end flex-1">
                              {log.sleep_duration_minutes != null && (
                                <span className="text-purple-600">{formatDuration(log.sleep_duration_minutes)}</span>
                              )}
                              {log.sleep_deep_minutes != null && (
                                <span className="text-indigo-600">{t('wellness.deepShort', 'Deep')} {formatDuration(log.sleep_deep_minutes)}</span>
                              )}
                              {log.sleep_light_minutes != null && (
                                <span className="text-sky-600">{t('wellness.lightShort', 'Light')} {formatDuration(log.sleep_light_minutes)}</span>
                              )}
                              {log.sleep_rem_minutes != null && (
                                <span className="text-violet-600">{t('wellness.remShort', 'REM')} {formatDuration(log.sleep_rem_minutes)}</span>
                              )}
                              {log.sleep_awake_minutes != null && (
                                <span className="text-gray-500">{t('wellness.awakeShort', 'Awake')} {formatDuration(log.sleep_awake_minutes)}</span>
                              )}
                            </div>
                            <div className="flex gap-1 ml-2">
                              <button
                                onClick={() => { setEditingLogId(log.id); setShowAddForm(false); setDeletingLogId(null) }}
                                className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                                title={t('common.edit', 'Edit')}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => { setDeletingLogId(log.id); setEditingLogId(null); setShowAddForm(false) }}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                title={t('common.delete', 'Delete')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}

      {/* Load more */}
      {weeksToLoad < 12 && (
        <button
          onClick={() => setWeeksToLoad((w) => Math.min(w + 4, 12))}
          className="w-full text-sm text-indigo-600 hover:text-indigo-800 py-2"
        >
          {t('wellness.loadMore', 'Load more weeks')}
        </button>
      )}
    </div>
  )
}

// --- Main WellnessCheckIn ---

export default function WellnessCheckIn() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showPhotoReview, setShowPhotoReview] = useState(null)

  const createMutation = useMutation({
    mutationFn: (data) => dailyActivityAPI.createLog(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityMultiWeek'] })
      queryClient.invalidateQueries({ queryKey: ['dailyActivityLatest'] })
      setShowPhotoReview(null)
      toast.success(t('wellness.saved', 'Sleep entry saved'))
    },
    onError: (err) => toast.error(err?.response?.data?.detail || t('common.error', 'Error')),
  })

  const handleScreenshotData = (data) => {
    // Data extracted from screenshot — show in form for review before saving
    setShowPhotoReview({
      log_date: data.log_date || new Date().toISOString().split('T')[0],
      sleep_duration_minutes: data.sleep_duration_minutes ?? '',
      sleep_deep_minutes: data.sleep_deep_minutes ?? '',
      sleep_light_minutes: data.sleep_light_minutes ?? '',
      sleep_rem_minutes: data.sleep_rem_minutes ?? '',
      sleep_awake_minutes: data.sleep_awake_minutes ?? '',
    })
  }

  return (
    <div className="space-y-6">
      <SleepPhotoUpload onDataExtracted={handleScreenshotData} />

      {/* Photo review form — user reviews extracted data before saving */}
      {showPhotoReview && (
        <div className="card">
          <div className="card-body">
            <h4 className="font-medium text-gray-900 mb-3">
              {t('wellness.reviewExtracted', 'Review Extracted Data')}
            </h4>
            <SleepEntryForm
              initialData={showPhotoReview}
              onSave={(data) => createMutation.mutate(data)}
              onCancel={() => setShowPhotoReview(null)}
              isPending={createMutation.isPending}
              t={t}
            />
          </div>
        </div>
      )}

      <LatestSleepCard />
      <SleepHistory />
    </div>
  )
}
